/**
 * Job costing math (PRD v2 §7, V1 tail) — "did we make money on this job?".
 *
 * Jobber cannot answer this at the invoice or on recurring work; Servwell must. The
 * arithmetic is pure and lives here so the API computes it (fat server) while the
 * rules stay unit-testable in isolation.
 *
 * Two honesty rules are baked into the shape of the result, not bolted on in the UI:
 *
 *  1. Labor logged by someone with NO cost rate contributes 0 cents but is counted in
 *     `unratedMinutes`. A missing rate must never masquerade as free labor — the caller
 *     is handed the evidence and is expected to say so out loud.
 *  2. `basis` states which revenue number the margin was measured against. An estimate
 *     compared to a quote is not the same claim as one compared to a real invoice, and
 *     the screen has to be able to tell the difference.
 */
import { roundHalfUp } from './money';

/** What a job's revenue was measured against. `none` = nothing billable exists yet. */
export type CostingBasis = 'invoiced' | 'quoted' | 'none';

export const JOB_COST_KINDS = [
  'material',
  'subcontractor',
  'equipment',
  'permit',
  'other',
] as const;
export type JobCostKind = (typeof JOB_COST_KINDS)[number];

export const TIME_ENTRY_SOURCES = ['timer', 'manual'] as const;
export type TimeEntrySource = (typeof TIME_ENTRY_SOURCES)[number];

/** A closed stretch of labor. Open (running) entries are excluded by the caller. */
export interface CostingTimeEntry {
  startedAt: Date;
  endedAt: Date | null;
  /** Cents per hour, snapshotted when the entry was written. Null = rate unknown. */
  costRateCents: number | null;
}

export interface CostingCostRow {
  totalCostCents: number;
}

export interface CostingInput {
  timeEntries: CostingTimeEntry[];
  costs: CostingCostRow[];
  /** Sum of issued (non-draft, non-void) invoices on the job. */
  invoicedCents: number;
  /** Sum of approved quotes converted to the job — the fallback basis. */
  quotedCents: number;
}

export interface JobCosting {
  basis: CostingBasis;
  revenueCents: number;
  invoicedCents: number;
  quotedCents: number;
  laborCents: number;
  laborMinutes: number;
  /** Minutes logged by someone with no cost rate — real hours, unknown cost. */
  unratedMinutes: number;
  materialCents: number;
  totalCostCents: number;
  marginCents: number;
  /** Margin as basis points of revenue, or null when there is no revenue to divide by. */
  marginBps: number | null;
}

/**
 * Whole minutes between two instants, never negative.
 *
 * ⚠️ Written a second time in SQL: `ENTRY_MINUTES` in `apps/api/src/services/reports/
 * revenue-share.ts` sums it per job and person for revenue share. Change both, or neither;
 * `apps/api/test-pg/revenue-page.test.ts` pins the two on the same entries (a half minute
 * rounds up in both).
 */
export function entryMinutes(startedAt: Date, endedAt: Date): number {
  const ms = endedAt.getTime() - startedAt.getTime();
  return ms <= 0 ? 0 : Math.round(ms / 60000);
}

/**
 * The hours a stopped time entry bills (Inventory v2 ticket 13): its whole minutes ÷ 60, to the
 * hundredth, rounded half up (1 h 20 min = 1.33 h). Billing counts in hundredths, as parts do.
 */
export function hoursOfMinutes(minutes: number): number {
  return Math.round((Math.max(0, minutes) * 100) / 60) / 100;
}

/** Cost of `minutes` at an hourly rate in cents. */
export function laborCostCents(minutes: number, hourlyCostCents: number): number {
  return roundHalfUp((minutes / 60) * hourlyCostCents);
}

export function computeJobCosting(input: CostingInput): JobCosting {
  let laborCents = 0;
  let laborMinutes = 0;
  let unratedMinutes = 0;

  for (const e of input.timeEntries) {
    if (!e.endedAt) continue; // a running timer has not produced cost yet
    const minutes = entryMinutes(e.startedAt, e.endedAt);
    laborMinutes += minutes;
    if (e.costRateCents === null) unratedMinutes += minutes;
    else laborCents += laborCostCents(minutes, e.costRateCents);
  }

  const materialCents = input.costs.reduce((sum, c) => sum + c.totalCostCents, 0);
  const totalCostCents = laborCents + materialCents;

  const basis: CostingBasis =
    input.invoicedCents > 0 ? 'invoiced' : input.quotedCents > 0 ? 'quoted' : 'none';
  const revenueCents =
    basis === 'invoiced'
      ? input.invoicedCents
      : basis === 'quoted'
        ? input.quotedCents
        : 0;

  const marginCents = revenueCents - totalCostCents;
  return {
    basis,
    revenueCents,
    invoicedCents: input.invoicedCents,
    quotedCents: input.quotedCents,
    laborCents,
    laborMinutes,
    unratedMinutes,
    materialCents,
    totalCostCents,
    marginCents,
    marginBps: revenueCents > 0 ? Math.round((marginCents / revenueCents) * 10000) : null,
  };
}

/** "3h 25m" / "45m" / "—" for a zero duration. Shared so every surface reads alike. */
export function formatMinutes(minutes: number): string {
  if (minutes <= 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
